/* Marastoni market-aware display currency selector for tile m² prices.
   - Denmark defaults to DKK
   - Europe defaults to EUR
   - Rest of world defaults to USD
   EUR and USD are always floored to the nearest 10 for the display price.
   Rates are fetched client-side and cached; checkout currency remains controlled by Shopify Markets. */
(() => {
  if (typeof window === 'undefined' || customElements.get('marastoni-currency-price')) return;

  const API = 'https://api.frankfurter.dev/v2/rates';
  const CACHE_MS = 12 * 60 * 60 * 1000;
  const ratePromises = new Map();
  const SYMBOLS = { DKK: '', EUR: '', USD: '' };
  const DISPLAY_LABELS = { DKK: 'DKK', EUR: '€', USD: 'USD' };
  const TARGETS = ['DKK', 'EUR', 'USD'];

  // Network fallback only. Live rates are preferred and normally replace these values.
  const DKK_EUR = 1 / 7.46038;
  const EUR_USD = 1.13;

  function cacheKey(base) {
    return `marastoni:display-rates:v2:${base}`;
  }

  function readCache(base) {
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey(base)) || 'null');
      if (!cached || !cached.rates || !Number.isFinite(Number(cached.savedAt))) return null;
      if (Date.now() - Number(cached.savedAt) > CACHE_MS) return null;
      return cached.rates;
    } catch (_) {
      return null;
    }
  }

  function writeCache(base, rates) {
    try {
      localStorage.setItem(cacheKey(base), JSON.stringify({ rates, savedAt: Date.now() }));
    } catch (_) {}
  }

  function fallbackRates(base) {
    const fallback = { DKK: null, EUR: null, USD: null };
    if (base === 'DKK') {
      fallback.DKK = 1;
      fallback.EUR = DKK_EUR;
      fallback.USD = DKK_EUR * EUR_USD;
      return fallback;
    }
    if (base === 'EUR') {
      fallback.DKK = 1 / DKK_EUR;
      fallback.EUR = 1;
      fallback.USD = EUR_USD;
      return fallback;
    }
    if (base === 'USD') {
      fallback.DKK = 1 / (DKK_EUR * EUR_USD);
      fallback.EUR = 1 / EUR_USD;
      fallback.USD = 1;
      return fallback;
    }

    // Shopify exposes the active market rate relative to the shop currency.
    // Marastoni's shop currency is DKK, so this gives us a safe fallback bridge.
    const shopifyRate = Number(window.Shopify?.currency?.rate);
    const shopifyActive = String(window.Shopify?.currency?.active || '').toUpperCase();
    if (shopifyActive === base && Number.isFinite(shopifyRate) && shopifyRate > 0) {
      const sourceToDkk = 1 / shopifyRate;
      fallback.DKK = sourceToDkk;
      fallback.EUR = sourceToDkk * DKK_EUR;
      fallback.USD = sourceToDkk * DKK_EUR * EUR_USD;
    }
    return fallback;
  }

  async function fetchRates(base) {
    const cached = readCache(base);
    if (cached) return cached;

    const quotes = TARGETS.filter(code => code !== base);
    const rates = { DKK: null, EUR: null, USD: null, [base]: 1 };

    try {
      if (quotes.length) {
        const url = `${API}?base=${encodeURIComponent(base.toLowerCase())}&quotes=${encodeURIComponent(quotes.join(',').toLowerCase())}`;
        const response = await fetch(url, { mode: 'cors', cache: 'default' });
        if (!response.ok) throw new Error(`FX ${response.status}`);
        const rows = await response.json();
        for (const row of rows) {
          const quote = String(row.quote || '').toUpperCase();
          const rate = Number(row.rate);
          if (TARGETS.includes(quote) && Number.isFinite(rate) && rate > 0) rates[quote] = rate;
        }
      }

      if (TARGETS.every(code => Number.isFinite(Number(rates[code])) && Number(rates[code]) > 0)) {
        writeCache(base, rates);
        return rates;
      }
    } catch (_) {}

    const fallback = fallbackRates(base);
    for (const code of TARGETS) {
      if (!Number.isFinite(Number(rates[code])) || Number(rates[code]) <= 0) rates[code] = fallback[code];
    }
    return rates;
  }

  function getRates(base) {
    if (!ratePromises.has(base)) ratePromises.set(base, fetchRates(base));
    return ratePromises.get(base);
  }

  class MarastoniCurrencyPrice extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.sourceMinor = Number(this.dataset.sourceMinor);
      this.sourceCurrency = String(this.dataset.sourceCurrency || 'DKK').toUpperCase();
      this.defaultCurrency = String(this.dataset.defaultCurrency || 'DKK').toUpperCase();
      this.country = String(this.dataset.country || '').toUpperCase();
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';

      this.amountEl = this.querySelector('[data-currency-amount]');
      this.symbolEl = this.querySelector('[data-currency-symbol]');
      this.codeEl = this.querySelector('[data-currency-code]');
      this.trigger = this.querySelector('[data-currency-trigger]');
      this.menu = this.querySelector('[data-currency-menu]');
      this.options = Array.from(this.querySelectorAll('[data-currency-option]'));
      this.priceBlock = this.closest('.product-info__block-item');
      if (!this.amountEl || !this.codeEl || !this.trigger || !this.menu || !Number.isFinite(this.sourceMinor)) return;

      this.trigger.addEventListener('click', () => this.toggleMenu(), { signal });
      this.trigger.addEventListener('keydown', event => {
        if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.openMenu();
          this.menu.querySelector('[aria-selected="true"]')?.focus();
        }
      }, { signal });

      this.options.forEach(option => option.addEventListener('click', () => {
        const currency = option.dataset.currencyOption;
        this.remember(currency);
        this.setCurrency(currency);
        this.closeMenu();
        this.trigger.focus({ preventScroll: true });
      }, { signal }));

      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closeMenu();
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.menu.hidden) {
          this.closeMenu();
          this.trigger.focus({ preventScroll: true });
        }
      }, { signal });

      const preferred = this.recall();
      const initial = TARGETS.includes(preferred) ? preferred : this.defaultCurrency;
      this.setCurrency(TARGETS.includes(initial) ? initial : 'DKK');
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    preferenceKey() {
      return `marastoni:display-currency:v2:${this.country || 'global'}`;
    }

    remember(currency) {
      try { sessionStorage.setItem(this.preferenceKey(), currency); } catch (_) {}
    }

    recall() {
      try { return String(sessionStorage.getItem(this.preferenceKey()) || '').toUpperCase(); } catch (_) { return ''; }
    }

    toggleMenu() {
      this.menu.hidden ? this.openMenu() : this.closeMenu();
    }

    openMenu() {
      this.menu.hidden = false;
      this.classList.add('is-open');
      this.priceBlock?.classList.add('marastoni-currency-layer-open');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    closeMenu() {
      this.menu.hidden = true;
      this.classList.remove('is-open');
      this.priceBlock?.classList.remove('marastoni-currency-layer-open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    async setCurrency(currency) {
      if (!TARGETS.includes(currency)) return;
      const token = Symbol(currency);
      this.renderToken = token;

      // If the selected display currency is already the Shopify source currency,
      // render immediately while still refreshing selection state.
      if (currency === this.sourceCurrency) {
        this.render(currency, this.sourceMinor / 100);
        return;
      }

      const rates = await getRates(this.sourceCurrency);
      if (!this.isConnected || this.renderToken !== token) return;
      const rate = Number(rates?.[currency]);
      if (!Number.isFinite(rate) || rate <= 0) {
        this.render(this.sourceCurrency, this.sourceMinor / 100);
        return;
      }
      this.render(currency, (this.sourceMinor / 100) * rate);
    }

    render(currency, rawAmount) {
      let amount = Number(rawAmount);
      if (!Number.isFinite(amount) || amount <= 0) return;

      // The agreed Marastoni display rule: EUR and USD always round DOWN to whole tens.
      if (currency === 'EUR' || currency === 'USD') {
        const floored = Math.floor((amount + 1e-9) / 10) * 10;
        amount = floored > 0 ? floored : Math.floor(amount);
      } else {
        amount = Math.round((amount + Number.EPSILON) * 100) / 100;
      }

      const isWhole = Math.abs(amount - Math.round(amount)) < 1e-9;
      const formatter = new Intl.NumberFormat(this.locale, {
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: currency === 'DKK' && !isWhole ? 2 : 0
      });

      this.symbolEl.textContent = SYMBOLS[currency] || '';
      this.amountEl.textContent = formatter.format(amount);
      this.codeEl.textContent = DISPLAY_LABELS[currency] || currency;
      this.dataset.displayCurrency = currency;

      this.options.forEach(option => {
        option.setAttribute('aria-selected', String(option.dataset.currencyOption === currency));
      });

      // Keep the tile calculator in the same product panel synced with the
      // market-default or manually selected display currency. The calculator
      // intentionally uses the same rounded m² display price so its total
      // matches what the customer sees above it.
      const productRoot = this.closest('.product-info');
      if (productRoot) {
        productRoot.dataset.marastoniDisplayCurrency = currency;
        productRoot.dataset.marastoniDisplayUnitPrice = String(amount);
        productRoot.dispatchEvent(new CustomEvent('marastoni:currency-change', {
          detail: { currency, unitPrice: amount },
          bubbles: false
        }));
      }
    }
  }

  customElements.define('marastoni-currency-price', MarastoniCurrencyPrice);
})();
